import React from 'react';
import { Box, Card, CircularProgress, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import {
  useGetFacilitySummaryQuery,
  useGetFacilityByCenterQuery,
  useGetFacilityByIssueTypeQuery,
} from '../../../services/ReportingService';
import { useGetCentersQuery, useGetIssueTypesQuery } from '../../../services/DimensionService/dimensionApi';
import {
  WorkOrderReportHeader,
  WorkOrderReportFilters,
  WorkOrderReportSummaryCards,
  WorkOrderReportCharts,
} from '../components';
import { useWorkOrderReportFilters } from '../hooks';


const WorkOrderReportPage: React.FC = () => {
  const {
    filters, setFilters,
    appliedFilters,
    fromDate, setFromDate,
    toDate, setToDate,
    handleApplyFilters,
    handleResetFilters,
  } = useWorkOrderReportFilters();

  const { data: centers = [], isLoading: centersLoading } = useGetCentersQuery();
  const { data: issueTypes = [], isLoading: issueTypesLoading } = useGetIssueTypesQuery();

  const { data: summaryData, isLoading: summaryLoading } = useGetFacilitySummaryQuery(appliedFilters);
  const { data: centerData = [], isLoading: centerLoading } = useGetFacilityByCenterQuery(appliedFilters);
  const { data: issueTypeData = [], isLoading: issueTypeLoading } = useGetFacilityByIssueTypeQuery(appliedFilters);

  const isLoading = summaryLoading || centerLoading || issueTypeLoading;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
        {/* Page Header */}
        <Card
          sx={{
            minWidth: 1200,
            mx: 'auto',
            mb: 3,
            border: '1px solid #e2e8f0',
            borderRadius: 2,
          }}
        >
          <WorkOrderReportHeader />
          <WorkOrderReportFilters
            filters={filters}
            onFiltersChange={setFilters}
            fromDate={fromDate}
            onFromDateChange={setFromDate}
            toDate={toDate}
            onToDateChange={setToDate}
            centers={centers}
            centersLoading={centersLoading}
            issueTypes={issueTypes}
            issueTypesLoading={issueTypesLoading}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        </Card>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Report Content */}
        {!isLoading && summaryData && (
          <>
            <WorkOrderReportSummaryCards summaryData={summaryData} />
            <WorkOrderReportCharts
              summaryData={summaryData}
              centerData={centerData}
              issueTypeData={issueTypeData}
            />
          </>
        )}

        {/* Empty State */}
        {!isLoading && !summaryData && (
          <Box sx={{ minWidth: 1200, mx: 'auto', textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="#64748b">
              No data available for the selected filters
            </Typography>
            <Typography variant="body2" color="#64748b" mt={1}>
              Try adjusting your filter criteria
            </Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default WorkOrderReportPage;
